import { useEffect, useRef, type RefObject } from "react";

interface Options {
  enabled?: boolean;
  /** Wraps the header row and the rows -- defines how far the header may travel. */
  contentRef: RefObject<HTMLElement | null>;
  /** The header row itself. */
  headerRef: RefObject<HTMLElement | null>;
  /**
   * The horizontally scrollable wrapper, if any. Skipped when looking for the
   * vertical scrollport: its vertical overflow is only a side effect of
   * `overflow-x`, so treating it as the pin line would cancel out to 0.
   */
  skipRef?: RefObject<HTMLElement | null>;
  /** Extra px gap between the pin line and the header row. */
  offset?: number;
}

/**
 * AUT-197: px to translate a table header row down so it appears pinned while
 * the page scrolls, leaving the table at its natural full height (no nested
 * scrollbar). Mirrors the Lovable prototype's approach.
 *
 * Needed because these tables sit inside a horizontally scrollable wrapper,
 * which is a scrollport in BOTH axes -- so plain `position: sticky` resolves
 * against that wrapper and can never reach the page scroller.
 */
/**
 * Page-level activity (scroll / resize / layout changes) that any pinned header
 * needs to react to. Installed ONCE for the whole app rather than per table:
 * the default is stickyHeader=true, so a page with several tables would otherwise
 * attach that many subtree MutationObservers to document.body.
 */
const subscribers = new Set<() => void>();
let teardownShared: (() => void) | null = null;

function notifySubscribers() {
  subscribers.forEach((cb) => cb());
}

function subscribeToPageActivity(cb: () => void): () => void {
  subscribers.add(cb);

  if (!teardownShared) {
    window.addEventListener("scroll", notifySubscribers, { passive: true, capture: true });
    window.addEventListener("resize", notifySubscribers);

    // Content ABOVE a table (bulk-actions bar, banners, toolbars) can appear or
    // disappear, shifting the table without changing its own size.
    const resize = new ResizeObserver(notifySubscribers);
    resize.observe(document.body);

    // ...and a bar can be swapped in without changing the page's measured size
    // at all (same height, different node), which ResizeObserver alone misses.
    const mutations = new MutationObserver(notifySubscribers);
    mutations.observe(document.body, { childList: true, subtree: true });

    teardownShared = () => {
      window.removeEventListener("scroll", notifySubscribers, { capture: true } as EventListenerOptions);
      window.removeEventListener("resize", notifySubscribers);
      resize.disconnect();
      mutations.disconnect();
      teardownShared = null;
    };
  }

  return () => {
    subscribers.delete(cb);
    if (subscribers.size === 0) teardownShared?.();
  };
}

export function useStickyHeaderOffset({ enabled, contentRef, headerRef, skipRef, offset = 0 }: Options): void {
  const frameRef = useRef(0);
  const lastRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // The node we last wrote a transform to, so cleanup resets exactly that one.
    // Not a ref: by cleanup time headerRef.current may already point elsewhere.
    let styledHeader: HTMLElement | null = null;

    const measure = () => {
      frameRef.current = 0;
      const content = contentRef.current;
      const header = headerRef.current;
      if (!content || !header) return;

      // Default 0 = pin to the window top; only a genuine ancestor scrollport
      // overrides it.
      let pinTop = 0;
      let el: HTMLElement | null = content.parentElement;
      while (el) {
        if (el !== skipRef?.current && /(auto|scroll|overlay)/.test(window.getComputedStyle(el).overflowY)) {
          pinTop = el.getBoundingClientRect().top;
          break;
        }
        el = el.parentElement;
      }

      const contentRect = content.getBoundingClientRect();
      const headerHeight = header.getBoundingClientRect().height;
      const raw = pinTop + offset - contentRect.top;
      const max = Math.max(0, contentRect.height - headerHeight);
      const y = Math.round(Math.min(Math.max(raw, 0), max));

      // Written straight to the node, not through React state: this runs on
      // every scroll frame, and a re-render per frame is what makes the header
      // visibly lag behind a fast scroll.
      if (y !== lastRef.current) {
        lastRef.current = y;
        header.style.transform = y ? `translateY(${y}px)` : "";
        styledHeader = header;
      }
    };

    const onScroll = () => {
      if (frameRef.current) return;
      frameRef.current = requestAnimationFrame(measure);
    };

    measure();

    // Per-instance: only this table's own size changes.
    const ownResize = new ResizeObserver(onScroll);
    if (contentRef.current) ownResize.observe(contentRef.current);

    const unsubscribe = subscribeToPageActivity(onScroll);

    // Reset any offset left behind when the header stops being pinned.
    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      ownResize.disconnect();
      unsubscribe();
      if (styledHeader) styledHeader.style.transform = "";
      lastRef.current = null;
    };
  }, [enabled, contentRef, headerRef, skipRef, offset]);
}

interface FooterOptions {
  enabled?: boolean;
  contentRef: RefObject<HTMLElement | null>;
  /** The footer row(s) — the section to keep pinned to the bottom. */
  footerRef: RefObject<HTMLElement | null>;
  skipRef?: RefObject<HTMLElement | null>;
  /** Extra px gap between the pin line and the footer row. */
  offset?: number;
}

/**
 * AUT-317: the mirror of useStickyHeaderOffset for a table footer -- px to translate the footer
 * row UP so it appears pinned to the bottom of the viewport while the page scrolls, until the
 * table's own bottom reaches it. Same reason for a JS transform over `position: sticky`: these
 * tables sit inside a horizontally scrollable wrapper that is a scrollport in both axes.
 */
export function useStickyFooterOffset({ enabled, contentRef, footerRef, skipRef, offset = 0 }: FooterOptions): void {
  const frameRef = useRef(0);
  const lastRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    let styledFooter: HTMLElement | null = null;

    const measure = () => {
      frameRef.current = 0;
      const content = contentRef.current;
      const footer = footerRef.current;
      if (!content || !footer) return;

      // Default = pin to the window bottom; only a genuine ancestor scrollport overrides it.
      let pinBottom = window.innerHeight;
      let el: HTMLElement | null = content.parentElement;
      while (el) {
        if (el !== skipRef?.current && /(auto|scroll|overlay)/.test(window.getComputedStyle(el).overflowY)) {
          pinBottom = el.getBoundingClientRect().bottom;
          break;
        }
        el = el.parentElement;
      }

      const contentRect = content.getBoundingClientRect();
      const footerHeight = footer.getBoundingClientRect().height;
      // Negative when the table extends below the pin line: lift the footer up to sit on it.
      const raw = pinBottom - offset - contentRect.bottom;
      const min = -Math.max(0, contentRect.height - footerHeight);
      const y = Math.round(Math.max(Math.min(raw, 0), min));

      if (y !== lastRef.current) {
        lastRef.current = y;
        footer.style.transform = y ? `translateY(${y}px)` : "";
        styledFooter = footer;
      }
    };

    const onScroll = () => {
      if (frameRef.current) return;
      frameRef.current = requestAnimationFrame(measure);
    };

    measure();

    const ownResize = new ResizeObserver(onScroll);
    if (contentRef.current) ownResize.observe(contentRef.current);

    const unsubscribe = subscribeToPageActivity(onScroll);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      ownResize.disconnect();
      unsubscribe();
      if (styledFooter) styledFooter.style.transform = "";
      lastRef.current = null;
    };
  }, [enabled, contentRef, footerRef, skipRef, offset]);
}
